import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import { useState } from "react";
import { hasElements } from "utils/arrays";

const CaseStatusCard = ({ loading, data, error }) => {
    const [language, setLanguage] = useState("en");
    
    if (loading) {
        return (
            <div className="case-status-card">
                <div className="case-status-card__header">
                    <div className="case-status-card__receipt">
                        <span className="case-status-card__receipt-label">{"Receipt Number"}</span>
                        <span className="case-status-card__receipt-number">{"--"}</span>
                    </div>
                </div>
                <LoadingPage />
            </div>
        );
    }

    if (!loading && error) {
        return (
            <div className="case-status-card">
                <div className="case-status-card__header">
                    <div className="case-status-card__receipt">
                        <span className="case-status-card__receipt-label">{"Receipt Number"}</span>
                        <span className="case-status-card__receipt-number">{"--"}</span>
                    </div>
                </div>
                <span className="case-status-card__no-results">
                    <Icon name="no-results" size="xlg" color="secondary" />
                    {error.response?.data?.message}
                </span>
            </div>
        );
    }

    if (!loading && !data) {
        return (
            <div className="case-status-card">
                <div className="case-status-card__header">
                    <div className="case-status-card__receipt">
                        <span className="case-status-card__receipt-label">{"Receipt Number"}</span>
                        <span className="case-status-card__receipt-number">{"--"}</span>
                    </div>
                </div>
                <span className="case-status-card__no-results">
                    <Icon name="no-results" size="lg" color="secondary" />
                    No evidence document found
                </span>
            </div>
        );
    }

    const getStatusBadgeClass = (statusText) => {
        const lowerStatus = statusText.toLowerCase();
        if (lowerStatus.includes("approved") || lowerStatus.includes("aprobado")) {
            return "case-status-card__status-badge--approved";
        }
        if (lowerStatus.includes("pending") || lowerStatus.includes("pendiente")) {
            return "case-status-card__status-badge--pending";
        }
        return "case-status-card__status-badge--info";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";

        // Handle formats like "09-05-2023 14:28:46" or "2023-09-05"
        const parts = dateStr.split(" ")[0];
        const dateParts = parts.includes("-") ? parts.split("-") : [];

        if (dateParts.length === 3) {
            // Check if format is MM-DD-YYYY or YYYY-MM-DD
            if (dateParts[0].length === 4) {
                // YYYY-MM-DD
                return `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
            } else {
                // MM-DD-YYYY
                return `${dateParts[0]}/${dateParts[1]}/${dateParts[2]}`;
            }
        }
        return dateStr;
    };

    const statusText = language === "en" ? data.current_case_status_text_en : data.current_case_status_text_es;

    const statusDesc = language === "en" ? data.current_case_status_desc_en : data.current_case_status_desc_es;

    const hasHistory = data.hist_case_status && data.hist_case_status.length > 0;

    const labels = {
        en: {
            receiptNumber: "Receipt Number",
            submitted: "Submitted",
            modified: "Last Modified",
            history: "Case History",
            noHistory: "No history available",
        },
        es: {
            receiptNumber: "Número de Recibo",
            submitted: "Enviado",
            modified: "Última Modificación",
            history: "Historial del Caso",
            noHistory: "No hay historial disponible",
        },
    };

    const t = labels[language];

    return (
        <div className="case-status-card">
            {/* Header */}
            <div className="case-status-card__header">
                <div className="case-status-card__receipt">
                    <span className="case-status-card__receipt-label">{t.receiptNumber}</span>
                    <span className="case-status-card__receipt-number">{data.receiptNumber}</span>
                </div>
                <span className="case-status-card__form-badge">{data.formType}</span>
            </div>

            {/* Language Toggle */}
            <div className="case-status-card__lang-toggle">
                <button
                    className={`case-status-card__lang-btn ${language === "en" ? "case-status-card__lang-btn--active" : ""}`}
                    onClick={() => setLanguage("en")}
                >
                    English
                </button>
                <button
                    className={`case-status-card__lang-btn ${language === "es" ? "case-status-card__lang-btn--active" : ""}`}
                    onClick={() => setLanguage("es")}
                >
                    Español
                </button>
            </div>

            {/* Status */}
            <div className="case-status-card__status">
                <span className={`case-status-card__status-badge ${getStatusBadgeClass(statusText)}`}>
                    <svg className="case-status-card__status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4" />
                        <circle cx="12" cy="12" r="10" />
                    </svg>
                    {statusText}
                </span>
                <p className="case-status-card__status-description">{statusDesc}</p>
            </div>

            {/* Dates */}
            <div className="case-status-card__dates">
                <div className="case-status-card__date-item">
                    <span className="case-status-card__date-label">{t.submitted}</span>
                    <span className="case-status-card__date-value">{formatDate(data.submittedDate)}</span>
                </div>
                <div className="case-status-card__date-item">
                    <span className="case-status-card__date-label">{t.modified}</span>
                    <span className="case-status-card__date-value">{formatDate(data.modifiedDate)}</span>
                </div>
            </div>

            {/* History */}
            <div className="case-status-card__history">
                <h4 className="case-status-card__history-title">{t.history}</h4>
                {hasHistory ? (
                    <ul className="case-status-card__timeline">
                        {hasElements(data.hist_case_status) &&
                            data.hist_case_status.map((item, index) => (
                                <li key={index} className="case-status-card__timeline-item">
                                    <span className="case-status-card__timeline-date">{formatDate(item.date)}</span>
                                    <p className="case-status-card__timeline-text">
                                        {language === "en" ? item.completed_text_en : item.completed_text_es}
                                    </p>
                                </li>
                            ))}
                    </ul>
                ) : (
                    <p className="case-status-card__history-empty">{t.noHistory}</p>
                )}
            </div>
        </div>
    );
};

export default CaseStatusCard;
